import { FormGroup, TextArea } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOcrkV1CustomControlData } from '@common/interfaces/ophthalmology/OorkAndOcrk/ocrkV1RequestCustomControlData';
import type { IServiceGetOne } from '@frontend/components/interfaces/serviceGetOne';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { PractitionerRoleInRequest, ProcessEntity, saveTypeEnum, ServiceCode } from '@common/enums/index';
import { getDisplayName } from '@common/utils/getDisplayName';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { SaveButton } from '@frontend/components/elements/carePathButtons/SaveButton';
import { SaveSendButton } from '@frontend/components/elements/carePathButtons/SaveSendButton';
import { computeFormGroupProps } from '@frontend/components/fields/FormGroup';
import { computeTextAreaProps } from '@frontend/components/fields/TextAreaField';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { renameErrorKeys } from '@frontend/utils/index';
import type { IOorkV1ProcedureDiagnosticReportAddEditProps } from '../../OORK/v1/OorkV1ProcedureDiagnosticReportAddEdit';
import { ButtonContainer } from '../../../../elements/common';
import { defaultTextAreaFormGroupFieldProps } from '../../OORK/v1/types';
import { NewOrEditRequestProcessContainer } from '../../utils/v1/NewOrEditRequestProcessContainer';
import { ServiceOrganizationProvider } from '../../utils/v1/ServiceOrganizationProvider';

interface IOcrkRequestAddEditProps extends IOorkV1ProcedureDiagnosticReportAddEditProps {
    isSaveButtonDisabled: boolean;
    saveProcedureDiagnosticReport: (value: saveTypeEnum) => Promise<void>;
}

export function OcrkV1RequestAddEdit(props: IOcrkRequestAddEditProps) {
    const [ocrkService, setOcrkService] = useState<IServiceGetOne | null>(null);
    const consultData = props.procedure.customControlData.consult;

    useEffect(() => {
        loadOcrkService();
    }, []);

    if (!ocrkService) {
        return null;
    }

    return (
        <NewOrEditRequestProcessContainer
            isFirstItem={props.isFirstItem}
            itemState={props.itemState}
            organizationName={props.currentProcedure?.performerOrganization.name}
            practitionerName={
                props.currentProcedure?.performerPractitioner
                    ? getDisplayName(props.currentProcedure.performerPractitioner)
                    : undefined
            }
            process={props.process}
            service={ocrkService}
            title={getServiceLabel(ServiceCode.OCRK, 'request', 'Consult')}
        >
            <ServiceOrganizationProvider
                errors={renameErrorKeys(props.errors, 'customControlData.consult.', '')}
                initialOrganizationId={consultData.selectedDiagnosticReportProviderOrganizationId}
                initialPractitionerId={consultData.selectedDiagnosticReportProviderPractitionerId}
                onChange={async (organizationId, practitionerId) => {
                    await props.handleChangeCustomControl({
                        consult: {
                            ...props.procedure.customControlData.consult,
                            selectedDiagnosticReportProviderOrganizationId: organizationId,
                            selectedDiagnosticReportProviderPractitionerId: practitionerId,
                        },
                    });
                    await props.validateOneCustomControl('consult.selectedDiagnosticReportProviderOrganizationId');
                    await props.validateOneCustomControl('consult.selectedDiagnosticReportProviderPractitionerId');
                }}
                onDoValidate={() => null}
                roleOfPractitionersInList={PractitionerRoleInRequest.specialist}
                serviceProviderServiceId={ocrkService.id}
            />

            <FormGroup
                {...computeFormGroupProps({
                    hasErrors: props.errors.has('customControlData.consult.question'),
                    isRequired: true,
                    label: translateKey('request.question'),
                })}
            >
                <TextArea
                    {...computeTextAreaProps({
                        ...defaultTextAreaFormGroupFieldProps,
                        error: props.errors.get('customControlData.consult.question'),
                        onChange: async (value) => await handleConsultChange('question', value),
                        onDoValidate: async () => await props.validateOneCustomControl('consult.question'),
                        placeholder: 'Vraag...',
                        value: consultData.question,
                    })}
                />
            </FormGroup>

            <FormGroup
                {...computeFormGroupProps({
                    hasErrors: props.errors.has('customControlData.consult.toelichting'),
                    label: translateKey('common.remarks'),
                })}
            >
                <TextArea
                    {...computeTextAreaProps({
                        ...defaultTextAreaFormGroupFieldProps,
                        error: props.errors.get('customControlData.consult.toelichting'),
                        onChange: async (value) => await handleConsultChange('toelichting', value),
                        onDoValidate: async () => await props.validateOneCustomControl('consult.toelichting'),
                        placeholder: 'Een toelichting, opmerkingen of vragen...',
                        value: consultData.toelichting,
                    })}
                />
            </FormGroup>

            <ButtonContainer
                alignRight
                buttons={[
                    <CancelButton
                        cancel={props.onCancel}
                        entity={ProcessEntity.procedure}
                        isExisting={!!props.request.id}
                        serviceCode={props.service.code}
                        serviceType={props.service.serviceType}
                        toastServiceLabelType="toast"
                    />,
                    <SaveButton
                        buttonDisabled={props.isSaveButtonDisabled}
                        save={async () => await props.saveProcedureDiagnosticReport(saveTypeEnum.draft)}
                    />,
                    <SaveSendButton
                        buttonDisabled={props.isSaveButtonDisabled}
                        saveSend={async () => await props.saveProcedureDiagnosticReport(saveTypeEnum.final)}
                    />,
                ]}
            />
        </NewOrEditRequestProcessContainer>
    );

    async function loadOcrkService() {
        const result = await fetchJson(`/api/services?code=${ServiceCode.OCRK}`, 'GET');
        if (result.json.items) {
            setOcrkService(result.json.items[0]);
        }
    }

    async function handleConsultChange<K extends keyof IOcrkV1CustomControlData>(name: K, value: string | null) {
        await props.handleChangeCustomControl({
            consult: {
                ...props.procedure.customControlData.consult,
                [name]: value,
            },
        });
    }
}
